import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Accordion from 'components/Accordion';
import Icon from 'components/ui/Icon';

import { SOptionCard, SOptionCode, SOptionLabel, SOptionsRow } from './styled';

/**
 * Identity-document attestation picker (EXI SI 23 — initial_user_requirements §6
 * rule 1): the PS must actively choose a document type; nothing is preselected.
 *
 * Controlled, standalone, flow-agnostic — it only emits the chosen
 * `IdentityDocumentType` (or `undefined` when skipped/deselected). Reused at
 * create-time (T08), one-shot edit (T09) and deferred qualify (T11).
 *
 * @typedef {'CNI' | 'PASSPORT' | 'RESIDENCE_PERMIT'} IdentityDocumentType
 *
 * @param {object} props
 * @param {IdentityDocumentType | undefined} [props.value] selected document type
 * @param {(value: IdentityDocumentType | undefined) => void} props.onChange
 * @param {boolean} [props.disabled]
 */
const OPTIONS = [
  // code shown on the card, i18n label key, emitted enum value
  { code: 'ID', labelKey: 'National ID', value: 'CNI' },
  { code: 'PP', labelKey: 'Passport', value: 'PASSPORT' },
  { code: 'RP', labelKey: 'Residence permit', value: 'RESIDENCE_PERMIT' },
];

const AttestationBlock = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();

  const handleSelect = (optionValue) => {
    if (disabled) return;
    // Re-clicking the selected card clears it (skip/deselect) — attestation is
    // optional at create time.
    onChange(value === optionValue ? undefined : optionValue);
  };

  return (
    <Accordion
      title={t('Identity document attestation')}
      subtitle={t('Document type (no default – regulatory requirement)')}
    >
      <SOptionsRow role="radiogroup" aria-label={t('Identity document attestation')}>
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <SOptionCard
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={t(option.labelKey)}
              data-id={`attestation-option-${option.value}`}
              selected={selected}
              disabled={disabled}
              onClick={() => handleSelect(option.value)}
            >
              <SOptionCode selected={selected}>{option.code}</SOptionCode>
              <SOptionLabel>{t(option.labelKey)}</SOptionLabel>
              {selected && <Icon icon="check" color="primary" />}
            </SOptionCard>
          );
        })}
      </SOptionsRow>
    </Accordion>
  );
};

AttestationBlock.propTypes = {
  value: PropTypes.oneOf(['CNI', 'PASSPORT', 'RESIDENCE_PERMIT']),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AttestationBlock;
